/**
 * ExcludeVendorDialog — captures an exclusion reason + category before
 * excluding a vendor from the All Vendors list.
 *
 * Wiring into VendorsTable is a separate task; this file only exports the dialog.
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { useAuth } from "@/hooks/useAuth";
import type { ExclusionCategory } from "@/types/negotiations";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<ExclusionCategory, string> = {
  direct_spend: "Direct spend",
  sensitive: "Sensitive",
  sole_source: "Sole source",
  other: "Other",
};

const CATEGORY_OPTIONS: ExclusionCategory[] = ["direct_spend", "sensitive", "sole_source", "other"];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ExcludeVendorDialogProps {
  /** The vendor to exclude. Pass null / undefined to keep the dialog dormant. */
  vendorName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful exclusion so the parent can react (e.g. refresh). */
  onExcluded?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ExcludeVendorDialog({
  vendorName,
  open,
  onOpenChange,
  onExcluded,
}: ExcludeVendorDialogProps) {
  const { user } = useAuth();
  const { setExcluded, isSettingExcluded } = useVendorContacts();

  const [category, setCategory] = useState<ExclusionCategory | "">("");
  const [reason, setReason] = useState("");

  // Reset form whenever the dialog opens for a (potentially new) vendor.
  useEffect(() => {
    if (open) {
      setCategory("");
      setReason("");
    }
  }, [open, vendorName]);

  const isReasonRequired = category === "other";
  const isReasonMissing = isReasonRequired && reason.trim().length === 0;
  const isCategoryMissing = category === "";
  const isConfirmDisabled = isSettingExcluded || isCategoryMissing || isReasonMissing;

  async function handleConfirm() {
    if (!vendorName || isCategoryMissing) return;

    await setExcluded(vendorName, true, {
      reason: reason.trim() || null,
      category: category as ExclusionCategory,
      userId: user?.id,
    });

    onOpenChange(false);
    onExcluded?.();
  }

  function handleCancel() {
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Exclude vendor</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {vendorName && (
            <p className="text-xs text-muted-foreground">
              Excluding <span className="font-medium text-foreground">{vendorName}</span> from the
              vendor list.
            </p>
          )}

          {/* Category select */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              Reason category <span className="text-destructive">*</span>
            </Label>
            <Select
              value={category}
              onValueChange={(val) => setCategory(val as ExclusionCategory)}
              disabled={isSettingExcluded}
            >
              <SelectTrigger className="h-8 text-xs" aria-label="Exclusion category">
                <SelectValue placeholder="Select a category…" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((cat) => (
                  <SelectItem key={cat} value={cat} className="text-xs">
                    {CATEGORY_LABELS[cat]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason textarea */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              Reason{isReasonRequired && <span className="text-destructive"> *</span>}
            </Label>
            <Textarea
              placeholder={
                isReasonRequired
                  ? "Please describe why this vendor is excluded…"
                  : "Optional — add context for the exclusion"
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isSettingExcluded}
              className="text-xs resize-none min-h-[72px]"
              aria-label="Exclusion reason"
            />
            {isReasonMissing && (
              <p className="text-[10px] text-destructive">
                A reason is required when category is &quot;Other&quot;.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={isSettingExcluded}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            className="gap-1.5"
          >
            {isSettingExcluded && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Exclude vendor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
